/**
 * ChurchPublicPage — public marketing landing for a single church.
 *
 * Route: /churches/:slug
 * Source: GET /api/church/public/by-slug/{slug}
 *
 * Honours the church's `theme` doc:
 *   - hero image, accent color (applied via CSS var --church-accent)
 *   - tagline, welcome message
 *   - social_links footer
 *   - section toggles (services/sermons/gallery/give/contact)
 *   - give_settings: card via Stripe Checkout + mobile money instructions
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, Navigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Church, Calendar, MapPin, Mail, Phone, Globe, Mic, BookOpen,
  Loader2, ArrowLeft, ExternalLink, PlayCircle, Heart, CreditCard,
  Smartphone, Facebook, Instagram, Youtube, Twitter, Music2, Copy,
  CheckCircle2, LogIn,
} from 'lucide-react';
import { MotherGeneralTributes } from '@/components/church/MotherGeneralTributes';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VALID_CATS = [
  { value: 'tithe', label: 'Tithe' },
  { value: 'offering', label: 'Offering' },
  { value: 'thanksgiving', label: 'Thanksgiving' },
  { value: 'missions', label: 'Missions' },
  { value: 'building', label: 'Building fund' },
  { value: 'youth', label: 'Youth' },
  { value: 'welfare', label: 'Welfare' },
  { value: 'special', label: 'Special' },
  { value: 'other', label: 'Other' },
];

const SOCIAL_META = [
  { key: 'facebook', icon: Facebook, label: 'Facebook' },
  { key: 'instagram', icon: Instagram, label: 'Instagram' },
  { key: 'youtube', icon: Youtube, label: 'YouTube' },
  { key: 'tiktok', icon: Music2, label: 'TikTok' },
  { key: 'twitter', icon: Twitter, label: 'Twitter' },
  { key: 'spotify', icon: Music2, label: 'Spotify' },
];

const ChurchPublicPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [giveOpen, setGiveOpen] = useState(false);

  const { data: church, isLoading, isError } = useQuery({
    queryKey: ['public-church', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/church/public/by-slug/${slug}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    enabled: !!slug,
    retry: false,
  });

  const giveSession = searchParams.get('give_session');
  const giveCancelled = searchParams.get('give_cancelled');
  const [pollResult, setPollResult] = useState(null);
  useEffect(() => {
    if (giveCancelled) {
      toast.info('Donation cancelled. Nothing was charged.');
      const next = new URLSearchParams(searchParams);
      next.delete('give_cancelled');
      setSearchParams(next, { replace: true });
    }
  }, [giveCancelled, searchParams, setSearchParams]);
  useEffect(() => {
    if (!giveSession) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts >= 6) return;
      attempts++;
      try {
        const res = await fetch(`${API_URL}/api/church/public/give/status/${giveSession}`);
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setPollResult(data);
          toast.success(`Thank you! Your ${data.currency} ${data.amount} gift was received.`);
          const next = new URLSearchParams(searchParams);
          next.delete('give_session');
          setSearchParams(next, { replace: true });
          return;
        }
        if (data.status === 'expired') {
          toast.error('Payment session expired.');
          const next = new URLSearchParams(searchParams);
          next.delete('give_session');
          setSearchParams(next, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [giveSession, searchParams, setSearchParams]);

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  // Provinces / districts / HQ are groupings — the canonical grouping page
  // (with its congregation list) lives at /church/<slug>.
  if (church?.is_grouping) return <Navigate to={`/church/${slug}`} replace />;
  if (isError || !church) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full"><CardContent className="py-10 text-center">
          <Church className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-bold text-slate-700">Church not found</p>
          <p className="text-sm text-slate-500 mt-2">The link may be inactive or the slug is wrong.</p>
          <Link to="/" className="text-emerald-700 text-sm mt-4 inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-3 w-3" />Back home</Link>
        </CardContent></Card>
      </div>
    );
  }

  const theme = church.theme || {};
  const accent = theme.accent_color || '#0d9488';
  const sections = theme.sections || { services: true, sermons: true, gallery: false, give: true, contact: true };
  const give = theme.give_settings || {};
  const socials = theme.social_links || {};
  const giveEnabled = !!(give.enabled && sections.give && (give.card_enabled || give.mobile_money_enabled));

  // The accent gradient is only ever a stand-in. The moment the church has
  // uploaded a banner or a video it shows on its own — no tint, no scrim.
  const heroVideo = theme.hero_video_url || '';
  const heroImage = theme.hero_image_url || '';
  const hasHeroMedia = !!(heroVideo || heroImage);
  const heroStyle = heroVideo ? {} : heroImage ? {
    backgroundImage: `url(${heroImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  } : {
    backgroundImage: `linear-gradient(135deg, ${accent}, ${accent}cc, #064e3b)`,
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ '--church-accent': accent }} data-testid="church-public">
      <header className="text-white relative" style={heroStyle} data-testid="church-public-hero">
        {heroVideo && (
          <video src={heroVideo} poster={heroImage || undefined} autoPlay muted loop playsInline
                 className="absolute inset-0 h-full w-full object-cover"
                 data-testid="church-public-hero-video" />
        )}
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-20 relative"
             style={hasHeroMedia ? { textShadow: '0 2px 18px rgba(0,0,0,0.55)' } : undefined}>
          <Link to="/" className="text-white/80 text-xs hover:underline inline-flex items-center gap-1 mb-6"><ArrowLeft className="h-3 w-3" />Back home</Link>
          <div className="flex items-start gap-4 flex-wrap">
            {(theme.logo_url || church.logo_url) ? (
              <img src={theme.logo_url || church.logo_url} alt={church.name} className="h-20 w-20 rounded-2xl object-cover ring-4 ring-white/20 shadow-xl" />
            ) : (
              <div className="h-20 w-20 rounded-2xl bg-white/10 ring-4 ring-white/20 flex items-center justify-center"><Church className="h-10 w-10 text-white/80" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.3em] text-white/80">Church</p>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mt-1">{church.name}</h1>
              {theme.tagline && <p className="mt-2 text-base sm:text-lg text-white/90 italic">{theme.tagline}</p>}
              {(church.city || church.country) && (
                <p className="text-white/80 mt-2 text-sm flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[church.city, church.country].filter(Boolean).join(', ')}</p>
              )}
            </div>
            {giveEnabled && (
              <Button
                size="lg" onClick={() => setGiveOpen(true)} data-testid="church-public-give-btn"
                style={{ background: 'white', color: accent }}
                className="font-bold shadow-xl hover:shadow-2xl transition-shadow"
              >
                <Heart className="h-4 w-4 mr-2 fill-current" />Give
              </Button>
            )}
            <Link
              to={`/churches/${slug}/live`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-bold shadow-xl hover:shadow-2xl transition-shadow text-sm bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20"
              data-testid="church-public-watch-live"
            >
              <PlayCircle className="h-4 w-4" />Watch Live
            </Link>
            <Link
              to={`/login?type=member&church=${slug}&redirect=/member/portal`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-bold shadow-xl hover:shadow-2xl transition-shadow text-sm bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20"
              data-testid="church-public-member-login"
            >
              <LogIn className="h-4 w-4" />Member login
            </Link>
          </div>
          {(theme.welcome_message || church.description) && (
            <p className="mt-6 text-base text-white/90 max-w-2xl">{theme.welcome_message || church.description}</p>
          )}
          <p className="mt-4 text-xs text-white/70" data-testid="church-public-member-hint">
            Already part of {church.name}?{' '}
            <Link to={`/login?type=member&church=${slug}&redirect=/member/portal`}
                  className="underline hover:text-white">
              Sign in to your member area
            </Link>{' '}
            for your giving history and messages from your leaders.
          </p>
        </div>
      </header>

      {pollResult?.offering_id && (
        <div className="bg-emerald-50 border-b border-emerald-200" data-testid="church-give-success-banner">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <p className="text-sm text-emerald-900">
              <strong>Thank you</strong> — your gift of <strong>{pollResult.currency} {pollResult.amount}</strong> was received.
            </p>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {sections.services && (
          <Card data-testid="church-public-services">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" style={{ color: accent }} />Upcoming Services</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(church.upcoming_services || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic">No upcoming services scheduled.</p>
              ) : (
                <ul className="space-y-2">
                  {church.upcoming_services.map(s => (
                    <li key={s.id} className="flex items-center justify-between p-3 rounded-lg border" style={{ background: `${accent}10`, borderColor: `${accent}30` }} data-testid={`church-public-service-${s.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{s.title}</p>
                        <p className="text-xs text-slate-500 truncate">{s.location || 'Location TBD'}{s.notes ? ` · ${s.notes}` : ''}</p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="text-sm font-medium" style={{ color: accent }}>{s.service_date}</p>
                        {s.service_time && <p className="text-xs text-slate-500">{s.service_time}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {sections.sermons && (
          <Card data-testid="church-public-sermons">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Mic className="h-4 w-4" style={{ color: accent }} />Latest Sermons</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(church.sermons || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic">No published sermons yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {church.sermons.map(s => (
                    <div key={s.id} className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow" data-testid={`church-public-sermon-${s.id}`}>
                      {s.thumbnail_url && <img src={s.thumbnail_url} alt={s.title} className="w-full h-32 object-cover" />}
                      <div className="p-3 space-y-1">
                        <p className="font-medium text-slate-900 line-clamp-1">{s.title}</p>
                        <p className="text-xs text-slate-500">{s.speaker || '—'} · {s.sermon_date}</p>
                        {s.scripture_ref && <p className="text-xs" style={{ color: accent }}><BookOpen className="h-3 w-3 inline mr-1" />{s.scripture_ref}</p>}
                        {s.summary && <p className="text-xs text-slate-600 line-clamp-2">{s.summary}</p>}
                        <div className="flex gap-2 pt-2">
                          {s.audio_url && <a href={s.audio_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs hover:underline" style={{ color: accent }}><PlayCircle className="h-3.5 w-3.5" />Listen</a>}
                          {s.video_url && <a href={s.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs hover:underline" style={{ color: accent }}><PlayCircle className="h-3.5 w-3.5" />Watch</a>}
                        </div>
                        {(s.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {s.tags.slice(0, 3).map(t => <Badge key={t} variant="outline" className="text-[9px]">#{t}</Badge>)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {sections.gallery && (theme.gallery_images || []).length > 0 && (
          <Card data-testid="church-public-gallery">
            <CardHeader className="pb-3"><CardTitle className="text-base">Gallery</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {theme.gallery_images.map((src, i) => <img key={i} src={src} alt="" className="aspect-square object-cover rounded-lg" />)}
              </div>
            </CardContent>
          </Card>
        )}

        {sections.contact && (church.contact_email || church.contact_phone || church.website_url || church.address) && (
          <Card data-testid="church-public-contact">
            <CardHeader className="pb-3"><CardTitle className="text-base">Get in touch</CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-2 text-sm">
              {church.address && <p className="flex items-center gap-2 text-slate-700"><MapPin className="h-3.5 w-3.5 text-slate-400" />{church.address}</p>}
              {church.contact_email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /><a href={`mailto:${church.contact_email}`} className="hover:underline" style={{ color: accent }}>{church.contact_email}</a></p>}
              {church.contact_phone && <p className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{church.contact_phone}</p>}
              {church.website_url && <p className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-slate-400" /><a href={church.website_url} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1" style={{ color: accent }}>{church.website_url}<ExternalLink className="h-3 w-3" /></a></p>}
            </CardContent>
          </Card>
        )}

        {Object.values(socials).some(Boolean) && (
          <div className="flex flex-wrap justify-center gap-3 pt-4" data-testid="church-public-socials">
            {SOCIAL_META.filter(s => socials[s.key]).map(({ key, icon: Icon, label }) => (
              <a key={key} href={socials[key]} target="_blank" rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:scale-110 transition-transform"
                title={label} data-testid={`church-public-social-${key}`}>
                <Icon className="h-4 w-4" style={{ color: accent }} />
              </a>
            ))}
          </div>
        )}
      </main>

      <MotherGeneralTributes />

      <footer className="text-center text-xs text-slate-400 py-6">© {new Date().getFullYear()} {church.name}.</footer>

      {giveOpen && <GiveDialog church={church} theme={theme} onClose={() => setGiveOpen(false)} />}
    </div>
  );
};

// ============================================================
// GIVE DIALOG
// ============================================================
const GiveDialog = ({ church, theme, onClose }) => {
  const give = theme.give_settings || {};
  const accent = theme.accent_color || '#0d9488';
  const [method, setMethod] = useState(give.card_enabled ? 'card' : 'mobile');
  const [amount, setAmount] = useState((give.suggested_amounts || [25])[1] || 25);
  // Prefer the church's base currency (resolver) over the theme default; church currency
  // is now persisted per-record so UK church shows £, SA shows R automatically.
  const [currency, setCurrency] = useState(church.currency || give.default_currency || 'USD');
  const [category, setCategory] = useState('offering');
  const [donor, setDonor] = useState({ name: '', email: '', phone: '' });
  const [provider, setProvider] = useState((give.mobile_money?.providers || [{}])[0]?.name || 'EcoCash');
  const [mmResult, setMmResult] = useState(null);

  // Pull the live currency catalogue so admins can add custom currencies
  // (BWP/NAD/ZIG/FaithCoin/etc.) and they show up here automatically.
  const [allCurrencies, setAllCurrencies] = React.useState([
    'USD', 'ZIG', 'GBP', 'ZAR', 'EUR', 'KES', 'NGN', 'INR', 'AUD', 'CAD',
  ]);
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/fx/catalogue`)
      .then(r => r.json())
      .then(d => {
        if (!cancelled && Array.isArray(d?.items)) {
          setAllCurrencies(d.items.map(i => i.code));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Auto-detect donor currency from CF-IPCountry/Accept-Language on mount.
  // Falls back to church.currency when detection yields nothing.
  React.useEffect(() => {
    if (!allCurrencies || !allCurrencies.length) return;
    let cancelled = false;
    const allowed = allCurrencies.join(',');
    fetch(`${API_URL}/api/fx/detect-currency?allowed=${allowed}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d && d.currency) setCurrency(d.currency);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCurrencies]);

  // Live FX preview — show "≈ {base_currency} X.XX" when donor flips currency
  const [fxPreview, setFxPreview] = useState(null);
  React.useEffect(() => {
    const churchCcy = (church.currency || give.default_currency || 'USD').toUpperCase();
    if (!amount || currency === churchCcy) { setFxPreview(null); return; }
    let cancelled = false;
    fetch(`${API_URL}/api/fx/convert`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: parseFloat(amount) || 0, from_currency: currency, to_currency: churchCcy }),
    }).then(r => r.json()).then(d => {
      if (!cancelled && d && d.rate > 0) {
        setFxPreview({ base_ccy: churchCcy, base_amount: d.converted_amount, rate: d.rate });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [amount, currency, church.currency, give.default_currency]);

  // Standard types plus every active campaign, so a donor can give straight
  // to the Hospital Building appeal without needing to explain themselves.
  const [types, setTypes] = useState({ standard: VALID_CATS, campaigns: [] });
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/church/public/${church.id}/give/donation-types`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d?.standard) setTypes(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [church.id]);
  const pickedCampaignType = String(category || '').startsWith('campaign:');

  // Live campaigns this designation could support — donor decides.
  const [campaigns, setCampaigns] = useState([]);
  const [pickedCampaigns, setPickedCampaigns] = useState([]);
  React.useEffect(() => {
    let cancelled = false;
    setPickedCampaigns([]);
    if (pickedCampaignType) { setCampaigns([]); return undefined; }
    fetch(`${API_URL}/api/church/public/${church.id}/give/campaign-matches?category=${encodeURIComponent(category)}`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setCampaigns(Array.isArray(d?.matches) ? d.matches : []); })
      .catch(() => { if (!cancelled) setCampaigns([]); });
    return () => { cancelled = true; };
  }, [category, church.id, pickedCampaignType]);
  const toggleCampaign = (id) => setPickedCampaigns(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const stripeMut = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/church/public/${church.id}/give/stripe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount), currency, category,
          campaign_ids: pickedCampaigns,
          donor_name: donor.name, donor_email: donor.email,
          origin_url: window.location.origin,
          return_path: `/churches/${church.slug}`,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      return res.json();
    },
    onSuccess: (data) => { window.location.href = data.url; },
    onError: (e) => toast.error(e.message),
  });

  const mobileMut = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/church/public/${church.id}/give/mobile`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount), currency, category,
          campaign_ids: pickedCampaigns,
          donor_name: donor.name, donor_email: donor.email,
          donor_phone: donor.phone, provider,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      return res.json();
    },
    onSuccess: (data) => setMmResult(data),
    onError: (e) => toast.error(e.message),
  });

  const suggestedAmountsBase = give.suggested_amounts || [10, 25, 50, 100, 250];

  // Localize the suggested-amount ladder for the donor's selected currency.
  // Falls back to the base ladder when same-currency or rate unavailable.
  const [localizedSuggested, setLocalizedSuggested] = React.useState(null);
  React.useEffect(() => {
    const baseCcy = (church.currency || give.default_currency || 'USD').toUpperCase();
    if (!currency || currency === baseCcy) { setLocalizedSuggested(null); return; }
    let cancelled = false;
    const url = `${API_URL}/api/fx/suggested-amounts?amounts=${suggestedAmountsBase.join(',')}&from=${baseCcy}&to=${currency}`;
    fetch(url).then(r => r.json()).then(d => {
      if (!cancelled && d && Array.isArray(d.items) && d.items.length) {
        setLocalizedSuggested(d.items.map(it => it.rounded));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, church.currency, give.default_currency]);

  const suggestedAmounts = localizedSuggested || suggestedAmountsBase;
  const mmInstructions = give.mobile_money?.instructions || '';
  const mmProviders = give.mobile_money?.providers || [];
  const valid = parseFloat(amount) > 0 && (method !== 'mobile' || donor.phone.trim());

  if (mmResult) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="give-mm-success">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" />Almost there</DialogTitle>
            <DialogDescription>Quote this reference when you complete the payment on your phone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-xs uppercase tracking-wide text-slate-500">Reference</p>
              <p className="text-2xl font-bold tracking-wide" style={{ color: accent }}>{mmResult.reference}</p>
              <Button size="sm" variant="outline" className="mt-2"
                onClick={() => { navigator.clipboard.writeText(mmResult.reference); toast.success('Reference copied'); }}>
                <Copy className="h-3 w-3 mr-1" />Copy
              </Button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <p><strong>{mmResult.currency} {Number(mmResult.amount).toFixed(2)}</strong> to {mmResult.church_name}</p>
              {mmInstructions && <p className="mt-2 text-xs">{mmInstructions}</p>}
              {(mmResult.providers || []).length > 0 && (
                <ul className="mt-2 text-xs space-y-1">
                  {mmResult.providers.map((p, i) => (
                    <li key={i}><strong>{p.name}:</strong> {p.number}</li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-xs text-slate-500">{mmResult.message}</p>
          </div>
          <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="give-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Heart className="h-5 w-5 fill-current" style={{ color: accent }} />Give to {church.name}</DialogTitle>
          <DialogDescription>Your gift goes directly to {church.name}. Pick an amount + method.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(give.card_enabled || give.mobile_money_enabled) && (
            <div className="grid grid-cols-2 gap-2">
              {give.card_enabled && (
                <button type="button" onClick={() => setMethod('card')} data-testid="give-method-card"
                  className={`p-3 border-2 rounded-lg text-left transition-all ${method === 'card' ? 'border-current shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                  style={{ color: method === 'card' ? accent : '#475569' }}>
                  <CreditCard className="h-5 w-5 mb-1" />
                  <p className="text-sm font-medium">Card</p>
                  <p className="text-[10px] text-slate-500">Visa, Mastercard via Stripe</p>
                </button>
              )}
              {give.mobile_money_enabled && (
                <button type="button" onClick={() => setMethod('mobile')} data-testid="give-method-mobile"
                  className={`p-3 border-2 rounded-lg text-left transition-all ${method === 'mobile' ? 'border-current shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                  style={{ color: method === 'mobile' ? accent : '#475569' }}>
                  <Smartphone className="h-5 w-5 mb-1" />
                  <p className="text-sm font-medium">Mobile money</p>
                  <p className="text-[10px] text-slate-500">EcoCash, OneMoney etc.</p>
                </button>
              )}
            </div>
          )}

          <div>
            <Label>Amount</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {suggestedAmounts.map(a => (
                <Button key={a} variant={parseFloat(amount) === a ? 'default' : 'outline'} size="sm"
                  onClick={() => setAmount(a)} data-testid={`give-quick-${a}`}
                  style={parseFloat(amount) === a ? { background: accent } : {}}>
                  {a}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input type="number" min={give.min_amount || 1} step="0.01" value={amount}
                onChange={e => setAmount(e.target.value)} placeholder="Custom amount" data-testid="give-amount" />
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-28" data-testid="give-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">{allCurrencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {fxPreview && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1.5 inline-block" data-testid="give-fx-preview">
                ≈ {fxPreview.base_ccy} {Number(fxPreview.base_amount).toFixed(2)} <span className="text-emerald-500">(@ {fxPreview.rate.toFixed(4)})</span>
              </p>
            )}
          </div>

          <div>
            <Label>Giving type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="give-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">Standard types</SelectLabel>
                  {(types.standard || []).map(c => (
                    <SelectItem key={c.value} value={c.value} data-testid={`give-type-${c.value}`}>{c.label}</SelectItem>
                  ))}
                </SelectGroup>
                {(types.campaigns || []).length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-amber-600">Campaigns</SelectLabel>
                    {(types.campaigns || []).map(c => (
                      <SelectItem key={c.value} value={c.value} data-testid={`give-type-${c.campaign_id}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            {pickedCampaignType && (
              <p className="text-[11px] text-emerald-700 mt-1" data-testid="give-campaign-type-note">
                Your gift goes to the church and counts towards this campaign.
              </p>
            )}
          </div>

          {campaigns.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3" data-testid="give-campaign-matches">
              <p className="text-xs font-semibold text-amber-900">
                Put this towards a campaign?
              </p>
              <p className="text-[11px] text-amber-700 mb-2">
                Your gift is still recorded by the church — ticking also counts it towards the campaign total.
              </p>
              <div className="space-y-1.5">
                {campaigns.map(c => (
                  <label key={c.id} className="flex items-start gap-2 text-sm cursor-pointer"
                         data-testid={`give-campaign-option-${c.id}`}>
                    <input type="checkbox" className="mt-1 accent-amber-600"
                           checked={pickedCampaigns.includes(c.id)}
                           onChange={() => toggleCampaign(c.id)} />
                    <span className="min-w-0">
                      <span className="font-medium text-slate-800">{c.name}</span>
                      {c.target_amount > 0 && (
                        <span className="block text-[11px] text-slate-500">
                          {c.currency} {Number(c.raised_amount).toLocaleString()} of {Number(c.target_amount).toLocaleString()} raised · {c.scope}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div><Label>Your name <span className="text-xs text-slate-500 font-normal">(optional)</span></Label><Input value={donor.name} onChange={e => setDonor({ ...donor, name: e.target.value })} data-testid="give-donor-name" /></div>
            <div><Label>Your email <span className="text-xs text-slate-500 font-normal">(receipt)</span></Label><Input type="email" value={donor.email} onChange={e => setDonor({ ...donor, email: e.target.value })} data-testid="give-donor-email" /></div>
          </div>

          {method === 'mobile' && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Your phone *</Label><Input value={donor.phone} onChange={e => setDonor({ ...donor, phone: e.target.value })} placeholder="+263 77 123 4567" data-testid="give-donor-phone" /></div>
              <div><Label>Provider</Label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger data-testid="give-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {mmProviders.length > 0
                      ? mmProviders.map((p, i) => <SelectItem key={i} value={p.name}>{p.name}</SelectItem>)
                      : ['EcoCash', 'OneMoney', 'InnBucks', 'MTN MoMo', 'Other'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {method === 'card' ? (
            <Button onClick={() => stripeMut.mutate()} disabled={!valid || stripeMut.isPending} data-testid="give-stripe-submit" style={{ background: accent }}>
              {stripeMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              <CreditCard className="h-4 w-4 mr-1" />Continue to checkout
            </Button>
          ) : (
            <Button onClick={() => mobileMut.mutate()} disabled={!valid || mobileMut.isPending} data-testid="give-mobile-submit" style={{ background: accent }}>
              {mobileMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              <Smartphone className="h-4 w-4 mr-1" />Get payment details
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChurchPublicPage;
